import { Button, Table } from '@/components/ui/';
import { ColumnDef } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import {
  COLLATERAL_DECIMALS,
  useMarketStores,
} from '@/store/use-markets-store';
import {
  BigDecimal,
  bigIntToDecimalStr,
  bigIntToNum,
  formatDate,
  formatNumber,
  formatOrderType,
} from '@/utils/value-format';
import {
  symbolToBaseQuote,
  abbrFromSym,
  imgFromAbbr,
} from '@/utils/token-symbol';
import { cn } from '@/utils/cn';
import { useOrders } from '../api/get-orders';
import { getOrderValueFromPrice } from '@/features/trade/order/utils/math';
import { useCancelOrder } from '@/features/trade/order/api/cancel-order';
import { useOrdersStore } from '@/store/use-orders-store';
import { OrderType } from '@/types/enums';
import { useShallow } from 'zustand/react/shallow';
import { ArrowLeftRight } from 'lucide-react';
import useGeofenceCheck from '@/utils/geofence-check';
import { Link } from 'react-router-dom';
import { useUnifiedAccount } from '@/hooks';
import { useAccountStore } from '@/store/use-account-store';

// all relevant fields are in decimal strings
type OpenOrder = {
  id: string;
  orderType: OrderType;
  symbol: string;
  isBuy: boolean;
  size: bigint;
  value?: number;
  limitPrice: bigint;
  tpPrice: string;
  slPrice: string;
  markPrice: bigint;
  reduceOnly: boolean;
  postTime: number;
  // closeDisabled: boolean;
};

export default function OpenOrdersTable() {
  const { data: isSanctioned } = useGeofenceCheck();

  const { isLoading } = useOrders();
  const { address } = useUnifiedAccount();
  const newOrders = useOrdersStore(
    useShallow((state) => {
      const { orders, newOrderIds } = state.getOrders(address);
      return Array.from(newOrderIds, (id) => orders[id]).filter(
        (order) => order.orderType !== OrderType.MARKET,
      );
      // return Array.from(newOrderIds, (id) => ({
      //   ...orders[id],
      //   closeDisabled: ordersToPoll.some(([pollOrderId]) => pollOrderId === id),
      // })).filter((order) => order.orderType !== OrderType.MARKET);
    }),
  );
  const markets = useMarketStores((state) => ({
    marketData: state.marketData,
    marketSpec: state.marketSpec,
  }));
  // TODO: newOrder markets

  const { mutateAsync: cancelOrder } = useCancelOrder();

  const openOrders: OpenOrder[] = useMemo(() => {
    return newOrders
      .map((order) => {
        const orderValue = getOrderValueFromPrice(
          order.size,
          order.limitPrice,
          markets[order.symbol].marketSpec,
        );
        return {
          ...order,
          value: orderValue
            ? bigIntToNum(orderValue.bigint, COLLATERAL_DECIMALS)
            : undefined,
          markPrice: markets[order.symbol].marketData.markPrice,
          tpPrice: order.tpPrice
            ? bigIntToDecimalStr(
                order.tpPrice,
                markets[order.symbol].marketSpec.priceDecimals,
              )
            : '',
          slPrice: order.slPrice
            ? bigIntToDecimalStr(
                order.slPrice,
                markets[order.symbol].marketSpec.priceDecimals,
              )
            : '',
          // closeDisabled: order.closeDisabled,
        };
      })
      .sort((a, b) => b.postTime - a.postTime);
  }, [newOrders, markets]);

  const nonce = useAccountStore((state) => state.nonce);

  const columns = useMemo<ColumnDef<OpenOrder>[]>(
    () => [
      {
        accessorFn: ({ postTime }) =>
          formatDate(new Date(postTime), {
            showTime: true,
            showSeconds: true,
          }),
        header: 'TIME',
        meta: { width: 'md:w-[14.5%] hidden md:table-cell' },
        sortingFn: (a, b) => a.original.postTime - b.original.postTime,
      },
      {
        accessorFn: ({ orderType }) => formatOrderType(orderType),
        header: 'TYPE',
        meta: { width: 'md:w-[5.5%] hidden md:table-cell' },
      },
      {
        accessorKey: 'symbol',
        cell: ({ row }) => (
          <Link
            to={`/trade/${row.original.symbol}`}
            className="flex items-center gap-4"
          >
            <img
              src={imgFromAbbr(abbrFromSym(row.original.symbol))}
              title={row.original.symbol}
              className="h-5 w-5"
            />
            <span className="hidden font-sans md:inline">
              {symbolToBaseQuote(row.original.symbol)}
            </span>
            <span className="font-sans md:hidden">
              {abbrFromSym(row.original.symbol)} /{' '}
              <span
                className={cn(
                  'font-mono',
                  row.original.isBuy ? 'text-green' : 'text-red',
                )}
              >
                {row.original.isBuy ? 'Long' : 'Short'}
              </span>
            </span>
          </Link>
        ),
        header: 'MARKET',
        meta: { width: 'md:w-[12.5%] pl-6 md:pl-0' },
      },
      {
        accessorKey: 'isBuy',
        cell: (info) => (
          <p className={cn(info.getValue() ? 'text-green' : 'text-red')}>
            {info.getValue() ? 'Long' : 'Short'}
          </p>
        ),
        header: 'SIDE',
        meta: { width: 'md:w-[6%] hidden md:table-cell' },
      },
      {
        accessorKey: 'size',
        cell: ({ row }) =>
          `${bigIntToDecimalStr(
            row.original.size,
            markets[row.original.symbol].marketSpec.sizeDecimals,
          )} ${abbrFromSym(row.original.symbol)}`,
        header: 'SIZE',
        meta: { width: 'md:w-[7.5%]' },
        sortDescFirst: false,
        sortingFn: (a, b) =>
          bigIntToNum(
            a.original.size,
            markets[a.original.symbol].marketSpec.sizeDecimals,
          ) -
          bigIntToNum(
            b.original.size,
            markets[b.original.symbol].marketSpec.sizeDecimals,
          ),
      },
      {
        accessorKey: 'value',
        cell: ({ row }) =>
          formatNumber(row.original.value, { digits: 2, round: 'ceil' }),
        header: 'VALUE',
        meta: { width: 'hidden md:table-cell md:w-[7.5%]' },
        sortDescFirst: false,
        sortUndefined: 'last',
      },
      {
        accessorFn: ({ limitPrice, symbol }) =>
          formatNumber(
            bigIntToDecimalStr(
              limitPrice,
              markets[symbol].marketSpec.priceDecimals,
            ),
          ),
        header: 'LIMIT PRICE',
        meta: { width: 'md:w-[10%]' },
        sortingFn: (a, b) =>
          bigIntToNum(
            a.original.limitPrice,
            markets[a.original.symbol].marketSpec.priceDecimals,
          ) -
          bigIntToNum(
            b.original.limitPrice,
            markets[b.original.symbol].marketSpec.priceDecimals,
          ),
      },
      {
        accessorFn: ({ markPrice, symbol }) =>
          formatNumber(
            bigIntToDecimalStr(
              markPrice,
              markets[symbol].marketSpec.priceDecimals,
            ),
          ),
        header: 'MARK PRICE',
        meta: { width: 'md:w-[10%] hidden md:table-cell' },
        sortingFn: (a, b) =>
          bigIntToNum(
            a.original.markPrice,
            markets[a.original.symbol].marketSpec.priceDecimals,
          ) -
          bigIntToNum(
            b.original.markPrice,
            markets[b.original.symbol].marketSpec.priceDecimals,
          ),
      },
      {
        id: 'tp-sl',
        cell: ({ row }) => (
          <div className="flex items-center gap-4">
            <p className="text-ellipsis whitespace-nowrap">
              {row.original.tpPrice ? formatNumber(row.original.tpPrice) : '-'}{' '}
              /{' '}
              {row.original.slPrice ? formatNumber(row.original.slPrice) : '-'}
            </p>
          </div>
        ),
        header: 'TP/SL',
        meta: { width: 'md:w-[12.5%] hidden md:table-cell' },
      },
      {
        accessorFn: ({ reduceOnly }) => (reduceOnly ? 'Yes' : 'No'),
        header: 'REDUCE ONLY',
        meta: { width: 'md:w-[7.5%] hidden md:table-cell' },
      },
      {
        id: 'actions',
        cell: ({ row }) => (
          <div className="flex justify-end">
            <Button
              onClick={() =>
                cancelOrder({ id: row.original.id, showToast: true })
              }
              disabled={isSanctioned || nonce === undefined}
              className="h-10 rounded-none bg-black-alt px-4 py-2 text-sm font-normal uppercase tracking-wider text-white transition-colors duration-200 hover:bg-black-alt focus:outline-none sm:px-6"
            >
              CANCEL
            </Button>
          </div>
        ),
        meta: { width: 'md:w-[6%]' },
      },
    ],
    [isSanctioned, nonce],
  );

  return (
    <Table
      data={openOrders}
      loading={isLoading}
      columns={columns}
      // RowComponent={PositionRow}
      className="[&_th]:whitespace-nowrap"
    />
  );
}
